/* rein-announcement-bar-2026-09-23
 *
 * Rotating announcement messages with an optional per-message countdown.
 *
 * ⚠️ THE COUNTDOWN IS DEADLINE-DRIVEN BY DESIGN — READ BEFORE CHANGING.
 * Every countdown counts toward ONE absolute instant that the operator sets in
 * the theme editor (date + time + explicit UTC offset). Liquid renders that as
 * an ISO-8601 string carrying the offset, e.g. 2026-10-01T17:00:00-04:00, into
 * data-rein-ab-end. Because the instant is absolute and offset-qualified:
 *
 *   - every visitor computes the SAME remaining time, anywhere in the world;
 *   - nothing is stored per-visitor. There is deliberately NO localStorage and
 *     NO sessionStorage here, so a reload or a new visitor cannot restart it;
 *   - there is no duration/relative input anywhere in the schema, so a timer
 *     cannot silently become "20 minutes from whenever you arrived";
 *   - on expiry the message is REMOVED from rotation (hidden) — it never shows
 *     a zero state, never rolls over, never restarts.
 *
 * An empty or unparseable datetime renders the message WITHOUT a countdown.
 * There is no default duration fallback — a broken date must degrade to a
 * plain message, never to an invented deadline.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-rein-ab]');
  if (!root) return;

  var rotator = root.querySelector('[data-rein-ab-rotator]');
  if (!rotator) return;

  var msgs = Array.prototype.slice.call(rotator.querySelectorAll('[data-rein-ab-msg]'));
  if (!msgs.length) return;

  var reduceMotion = false;
  try {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* older engines: treat as motion allowed */ }

  var DWELL = reduceMotion ? 9000 : 5500;
  var idx = 0;
  var timer = null;
  var paused = false;

  /* ── countdown ───────────────────────────────────────────────────────── */

  function endTimeOf(el) {
    var raw = el.getAttribute('data-rein-ab-end');
    if (!raw) return null;
    var t = Date.parse(raw);
    return isNaN(t) ? null : t; // unparseable -> no countdown, never a default
  }

  function formatRemaining(ms) {
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600);  s -= h * 3600;
    var m = Math.floor(s / 60);    s -= m * 60;
    var pad = function (n) { return n < 10 ? '0' + n : '' + n; };
    if (d > 0) return d + 'd ' + h + 'h ' + pad(m) + 'm';
    if (h > 0) return h + 'h ' + pad(m) + 'm ' + pad(s) + 's';
    return m + 'm ' + pad(s) + 's';
  }

  /* Returns true if the message is still live. An expired one is hidden for
   * good: no zero state, no rollover. */
  function paintCountdown(el) {
    var out = el.querySelector('[data-rein-ab-cd]');
    if (!out) return true;
    var end = endTimeOf(el);
    if (end === null) {
      out.textContent = '';
      out.hidden = true;
      return true; // no/!invalid deadline -> plain message, still rotates
    }
    var left = end - Date.now();
    if (left <= 0) {
      // Clear the text before hiding. The element is display:none either way,
      // but leaving the last painted value behind means a stale "0m 12s"
      // lingers in the DOM and shows up in any text extraction of the bar.
      out.textContent = '';
      out.hidden = true;
      el.hidden = true;
      return false;
    }
    out.hidden = false;
    out.textContent = formatRemaining(left);
    return true;
  }

  function liveMessages() {
    return msgs.filter(function (m) { return !m.hidden; });
  }

  function tickCountdowns() {
    var changed = false;
    msgs.forEach(function (m) {
      if (m.hidden) return;
      if (!paintCountdown(m)) changed = true;
    });
    if (changed) {
      var live = liveMessages();
      if (!live.length) {
        // everything expired: take the whole bar down rather than show an
        // empty strip
        root.hidden = true;
        stop();
        return;
      }
      if (msgs[idx] && msgs[idx].hidden) show(0, true);
      applyStaticIfSingle();
    }
  }

  /* ── rotation ────────────────────────────────────────────────────────── */

  function show(n, immediate) {
    var live = liveMessages();
    if (!live.length) return;
    var target = live[((n % live.length) + live.length) % live.length];
    msgs.forEach(function (m) { m.classList.toggle('is-active', m === target); });
    idx = msgs.indexOf(target);
  }

  function next() {
    var live = liveMessages();
    if (live.length < 2) return;
    var pos = live.indexOf(msgs[idx]);
    show(pos + 1);
  }

  function start() {
    stop();
    if (liveMessages().length < 2) return;
    timer = window.setInterval(function () { if (!paused) next(); }, DWELL);
  }

  function stop() {
    if (timer) { window.clearInterval(timer); timer = null; }
  }

  /* A single enabled (or single surviving) message must render static — no
   * rotation, no absolute stacking. */
  function applyStaticIfSingle() {
    var single = liveMessages().length < 2;
    root.classList.toggle('rein-ab--static', single);
    if (single) { stop(); } else if (!timer) { start(); }
  }

  /* ── pause on hover AND keyboard focus ───────────────────────────────── */
  ['mouseenter', 'focusin'].forEach(function (ev) {
    root.addEventListener(ev, function () { paused = true; });
  });
  ['mouseleave', 'focusout'].forEach(function (ev) {
    root.addEventListener(ev, function () { paused = false; });
  });

  msgs.forEach(function (m) { paintCountdown(m); });
  var live = liveMessages();
  if (!live.length) { root.hidden = true; return; }
  show(0, true);
  applyStaticIfSingle();
  window.setInterval(tickCountdowns, 1000);
})();
